import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import type { PersistOptions } from 'zustand/middleware';
import type { StoreApi, UseBoundStore } from 'zustand';
import { createPersistAdapter } from './createPersistAdapter';

/**
 * Options for configuring a game store.
 */
export type GameStoreOptions = {
  /**
   * Unique save key for this store. Combined with savePrefix to form the
   * full storage key: `${savePrefix}_${saveKey}`.
   */
  saveKey: string;

  /**
   * Prefix for all save keys. Defaults to the VITE_GAME_PREFIX env var,
   * falling back to 'my-game'. Override per-store when needed.
   */
  savePrefix?: string;

  /**
   * Set to false to disable persistence entirely (useful for ephemeral stores
   * like UI state that should never be saved). Defaults to true.
   */
  persist?: boolean;

  /**
   * Extra options for Zustand persist() (e.g. `merge` when the saved JSON shape drifts).
   */
  persistConfig?: Partial<PersistOptions<object, object>>;
};

/**
 * The initializer function receives Zustand's `set` and `get` and must return
 * the full store shape (state + actions merged into one flat object).
 *
 * This matches Zustand's idiomatic pattern and keeps types simple.
 * Uses a loose store type to avoid persist+subscribeWithSelector middleware type conflicts.
 */
export type GameStoreInitializer<TStore extends object> = (
  set: {
    (partial: TStore | Partial<TStore> | ((state: TStore) => TStore | Partial<TStore>), replace?: false): void;
    (state: TStore | ((state: TStore) => TStore), replace: true): void;
  },
  get: () => TStore,
  store: StoreApi<TStore>,
) => TStore;

/**
 * A Zustand store enhanced with subscribeWithSelector middleware.
 * This gives you fine-grained subscriptions both inside React (via hooks)
 * and outside React (Excalibur actors, plain functions).
 *
 * Usage in React:
 *   const gold = useCurrencyStore(s => s.gold);
 *
 * Usage outside React (Excalibur, plain TS):
 *   const { gold } = useCurrencyStore.getState();
 *   useCurrencyStore.subscribe(s => s.gold, newGold => { ... });
 */
export type GameStore<TStore extends object> = UseBoundStore<
  StoreApi<TStore>
>;

/**
 * Store returned when `persist` is enabled (default). Exposes Zustand’s `persist`
 * API for tests and tooling (e.g. `rehydrate()` after mutating storage).
 */
export type PersistedGameStore<TStore extends object> = GameStore<TStore> & {
  persist: {
    setOptions: (options: Partial<PersistOptions<TStore, TStore>>) => void;
    clearStorage: () => void;
    rehydrate: () => Promise<void> | void;
    hasHydrated: () => boolean;
    onHydrate: (listener: (state: TStore) => void) => () => void;
    onFinishHydration: (listener: (state: TStore) => void) => () => void;
    getOptions: () => Partial<PersistOptions<TStore, TStore>>;
  };
};

/**
 * Factory that creates a typed, persistable, reactive Zustand store.
 *
 * Each domain (currency, gameStatus, dungeonCrawler, settings…) gets its own
 * store created by one call to `createGameStore`. Stores are independent —
 * no mega-store, no slice wiring needed.
 *
 * @example
 * export const useCurrencyStore = createGameStore<CurrencyStore>(
 *   { saveKey: 'currency' },
 *   (set, get) => ({
 *     gold: 0,
 *     addGold: (amount) => set({ gold: get().gold + amount }),
 *   })
 * );
 */
export function createGameStore<TStore extends object>(
  options: Omit<GameStoreOptions, 'persist'> & { persist?: true },
  initializer: GameStoreInitializer<TStore>,
): PersistedGameStore<TStore>;
export function createGameStore<TStore extends object>(
  options: Omit<GameStoreOptions, 'persist'> & { persist: false },
  initializer: GameStoreInitializer<TStore>,
): GameStore<TStore>;
export function createGameStore<TStore extends object>(
  options: GameStoreOptions,
  initializer: GameStoreInitializer<TStore>,
): PersistedGameStore<TStore> | GameStore<TStore> {
  const savePrefix =
    options.savePrefix ??
    (typeof import.meta !== 'undefined'
      ? (import.meta.env?.VITE_GAME_PREFIX ?? 'my-game')
      : 'my-game');

  const fullKey = `${savePrefix}_${options.saveKey}`;
  const shouldPersist = options.persist !== false;

  if (shouldPersist) {
    // Middleware order: persist wraps subscribeWithSelector so persist receives the base store.
    // This resolves the type mismatch (persist expects store with persist, subscribeWithSelector doesn't add it).
    return create<TStore>()(
      persist(
        subscribeWithSelector(initializer),
        {
          name: fullKey,
          storage: createPersistAdapter<TStore>(),
          ...(options.persistConfig ?? {}),
        } as PersistOptions<TStore, TStore>,
      ),
    ) as PersistedGameStore<TStore>;
  }

  return create<TStore>()(subscribeWithSelector(initializer));
}
